import { type Component, createMemo, For, Show } from 'solid-js'
import Button from '~/components/Button'
import Chip from '~/components/Chip'
import Icon from '~/components/Icon'
import Modal from '~/components/Modal'
import { useToast } from '~/components/useToast'
import type { AuditEventType } from '~/types/Audit'
import { formatDateTime } from '~/utils/settings'
import { actionStyle, actorLabel, changedFields, formatValue, hasContent, parseJson, prettyJson, severityStyle } from './audit-labels'

interface AuditEventModalProps {
  /** El evento a mostrar. `null` cierra el modal. */
  event: AuditEventType | null
  onClose: () => void
  /** Nombre del usuario que originó el evento, si se pudo resolver. */
  userName?: (userId: number | null) => string
}

/** Fila etiqueta + valor del bloque de resumen. */
const Dato: Component<{ label: string; children: import('solid-js').JSX.Element }> = props => (
  <div class="min-w-0">
    <p class="text-xs text-foreground-subtle">{props.label}</p>
    <div class="text-sm text-foreground break-words">{props.children}</div>
  </div>
)

/** Bloque de JSON crudo, plegado por defecto para no tapar lo importante. */
const BloqueJson: Component<{ title: string; value: Record<string, unknown> }> = props => (
  <Show when={hasContent(props.value)}>
    <details class="rounded-lg border border-border">
      <summary class="cursor-pointer px-3 py-2 text-sm font-medium text-foreground">{props.title}</summary>
      <pre class="max-h-80 overflow-auto border-t border-border bg-surface-muted px-3 py-2 text-xs text-foreground">{prettyJson(props.value)}</pre>
    </details>
  </Show>
)

/**
 * Detalle completo de un evento de auditoría.
 *
 * Lo primero es lo que cambió (`changed`, o la diferencia entre `before` y
 * `after` cuando el evento no la trae); el JSON crudo queda plegado debajo para
 * quien necesite el dato exacto.
 */
const AuditEventModal: Component<AuditEventModalProps> = props => {
  const toast = useToast()

  const cambios = createMemo(() => (props.event ? changedFields(props.event) : []))
  const antes = createMemo(() => parseJson(props.event?.before))
  const despues = createMemo(() => parseJson(props.event?.after))
  const datos = createMemo(() => parseJson(props.event?.data))
  const metadatos = createMemo(() => parseJson(props.event?.metadata))

  const copiarId = async () => {
    const id = props.event?.event_id
    if (!id) return
    try {
      await navigator.clipboard.writeText(id)
      toast.addToast({ title: 'Identificador copiado', type: 'success' })
    } catch {
      toast.addToast({ title: 'No se pudo copiar', type: 'danger' })
    }
  }

  return (
    <Modal isOpen={props.event !== null} onClose={props.onClose} size="xl">
      <Modal.Header>
        <Show when={props.event}>
          {evento => (
            <div class="flex flex-wrap items-center gap-2 pe-8">
              <Chip size="sm" variant="soft" color={actionStyle(evento().event_action).color} icon={actionStyle(evento().event_action).icon} class="shrink-0 whitespace-nowrap">
                {actionStyle(evento().event_action).label}
              </Chip>
              <h2 class="text-lg font-bold text-foreground break-all">{evento().event_type}</h2>
            </div>
          )}
        </Show>
      </Modal.Header>

      <Modal.Body>
        <Show when={props.event}>
          {evento => (
            <div class="space-y-4">
              {/* Resumen */}
              <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Dato label="Fecha y hora">{formatDateTime(evento().event_timestamp)}</Dato>
                <Dato label="Recurso">
                  <span class="font-medium">{evento().resource_type || '—'}</span>
                  <Show when={evento().resource_id}>
                    <span class="text-foreground-subtle"> #{evento().resource_id}</span>
                  </Show>
                </Dato>
                <Dato label="Usuario">{props.userName?.(evento().user_id) ?? (evento().user_id ? `#${evento().user_id}` : 'Sistema')}</Dato>
                <Dato label="Origen">{actorLabel(evento().actor_type)}</Dato>
                <Dato label="Severidad">
                  <Chip size="xs" variant="soft" color={severityStyle(evento().severity).color}>
                    {severityStyle(evento().severity).label}
                  </Chip>
                </Dato>
                <Dato label="Petición">
                  <Show when={evento().route} fallback="—">
                    <span class="font-mono text-xs">{evento().route}</span>
                  </Show>
                </Dato>
                <Dato label="Dirección IP">{evento().ip_address || '—'}</Dato>
                <Dato label="Entorno">{evento().environment || '—'}</Dato>
              </div>

              {/* Error, cuando lo hay: va arriba porque es el motivo de mirar el evento */}
              <Show when={evento().error_message || evento().error_code}>
                <div class="rounded-lg border border-danger-soft bg-danger-soft/30 p-3">
                  <p class="flex items-center gap-2 text-sm font-semibold text-danger">
                    <Icon icon="material-symbols-light:error-outline" size={18} />
                    {evento().error_code || 'Error'}
                  </p>
                  <Show when={evento().error_message}>
                    <p class="mt-1 text-sm text-danger break-words">{evento().error_message}</p>
                  </Show>
                  <Show when={evento().stack_trace}>
                    <pre class="mt-2 max-h-48 overflow-auto text-xs text-danger">{evento().stack_trace}</pre>
                  </Show>
                </div>
              </Show>

              {/* Lo que cambió */}
              <div>
                <h3 class="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Icon icon="material-symbols-light:difference-outline" size={18} />
                  Cambios ({cambios().length})
                </h3>
                <Show when={cambios().length > 0} fallback={<p class="text-sm text-foreground-subtle">Este evento no registra cambios de campos.</p>}>
                  <div class="overflow-x-auto rounded-lg border border-border">
                    <table class="w-full min-w-[32rem] text-sm">
                      <thead class="bg-surface-muted text-xs text-foreground-subtle">
                        <tr>
                          <th class="px-3 py-2 text-start font-medium">Campo</th>
                          <th class="px-3 py-2 text-start font-medium">Antes</th>
                          <th class="px-3 py-2 text-start font-medium">Después</th>
                        </tr>
                      </thead>
                      <tbody>
                        <For each={cambios()}>
                          {cambio => (
                            <tr class="border-t border-border align-top">
                              <td class="px-3 py-2 font-medium text-foreground">{cambio.field}</td>
                              <td class="px-3 py-2 text-foreground-muted line-through break-words">{formatValue(cambio.from)}</td>
                              <td class="px-3 py-2 text-foreground break-words">{formatValue(cambio.to)}</td>
                            </tr>
                          )}
                        </For>
                      </tbody>
                    </table>
                  </div>
                </Show>
              </div>

              {/* El dato exacto, para quien lo necesite */}
              <div class="space-y-2">
                <BloqueJson title="Estado anterior" value={antes()} />
                <BloqueJson title="Estado posterior" value={despues()} />
                <BloqueJson title="Datos del evento" value={datos()} />
                <BloqueJson title="Metadatos" value={metadatos()} />
              </div>

              {/* Trazabilidad */}
              <div class="grid grid-cols-1 gap-4 border-t border-border pt-3 md:grid-cols-3">
                <Dato label="Identificador del evento">
                  <span class="font-mono text-xs break-all">{evento().event_id}</span>
                </Dato>
                <Dato label="Identificador de la petición">
                  <span class="font-mono text-xs break-all">{evento().request_id || '—'}</span>
                </Dato>
                <Dato label="Navegador">
                  <span class="text-xs break-words">{evento().user_agent || '—'}</span>
                </Dato>
              </div>
            </div>
          )}
        </Show>
      </Modal.Body>

      <Modal.Footer>
        <div class="flex justify-end gap-2">
          <Button type="button" variant="soft" color="default" onClick={copiarId} prependIcon="material-symbols-light:content-copy-outline">
            Copiar identificador
          </Button>
          <Button type="button" onClick={props.onClose}>
            Cerrar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default AuditEventModal
